/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Feedback Context Utility - Auto-capture context and metadata for feedback
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

import useChallengeStore, {
  useChallengeV2 as useChallengeStoreV2,
  selectCurrentQuestionId
} from '../hooks/useChallenge'
import versionService from '../services/versionService'
import { versionConfig } from '../config/versionConfig'

/**
 * Capture the current challenge/stage/question context
 * @returns {Object} Context object with challengeId, stageId, questionId
 */
export const getChallengeContext = () => {
  const v1State = useChallengeStore.getState()
  const v2State = useChallengeStoreV2.getState()

  return {
    challengeId: v2State.currentChallenge?.id || v1State.currentChallenge?.id || null,
    stageId: v2State.currentStage?.id || v1State.currentStage?.id || null,
    questionId: selectCurrentQuestionId(v2State) || v1State.currentQuestionContent?.id || null
  }
}

/**
 * Capture browser and device metadata
 * @returns {Object} Metadata object with browser, device, and session info
 */
export const getMetadata = () => {
  // Get version info for build IDs
  const versionInfo = versionService.getVersionInfo()

  // Server build ID: prefer cached value from version check, fall back to UI build ID
  // (they should match for same deployment since server reads client/dist/meta.json)
  const serverBuildId =
    versionInfo.serverBuildId ||
    versionInfo.serverData?.buildId ||
    versionConfig.buildId ||
    'unknown'

  const metadata = {
    // Build information
    uiBuildId: versionConfig.buildId || 'unknown',
    apiBuildId: serverBuildId,

    // Browser information
    browser: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,

    // Screen information
    screenResolution: `${window.screen.width}x${window.screen.height}`,
    viewportSize: `${window.innerWidth}x${window.innerHeight}`,
    colorDepth: window.screen.colorDepth,
    pixelRatio: window.devicePixelRatio,

    // Current page information
    url: window.location.href,
    pathname: window.location.pathname,
    referrer: document.referrer,

    // Timestamp
    timestamp: new Date().toISOString(),

    // Connection information (if available)
    connection: navigator.connection
      ? {
          effectiveType: navigator.connection.effectiveType,
          downlink: navigator.connection.downlink,
          rtt: navigator.connection.rtt,
          saveData: navigator.connection.saveData
        }
      : null,

    // Memory information (if available)
    memory: navigator.deviceMemory
      ? {
          deviceMemory: navigator.deviceMemory
        }
      : null
  }

  return metadata
}

/**
 * Get complete feedback context including challenge context and metadata
 * @returns {Object} Complete context object
 */
export const getCompleteFeedbackContext = () => {
  const challengeContext = getChallengeContext()
  const metadata = getMetadata()

  return {
    ...challengeContext,
    metadata
  }
}

/**
 * Get human-readable context description for UI display
 * @returns {string} Human-readable context description
 */
export const getContextDescription = () => {
  const context = getChallengeContext()
  const v1State = useChallengeStore.getState()
  const v2State = useChallengeStoreV2.getState()

  // Prefer v2 state when available, fall back to v1
  const challenge = v2State.currentChallenge || v1State.currentChallenge
  const stage = v2State.currentStage || v1State.currentStage
  const isV2Active = !!v2State.currentChallenge

  const parts = []

  if (context.challengeId && challenge) {
    parts.push(`Challenge: ${challenge.name || challenge.id}`)
  }

  if (context.stageId && stage) {
    parts.push(`Stage: ${stage.name || stage.id}`)
  }

  if (context.questionId) {
    let questionNumber
    let totalQuestions

    if (isV2Active) {
      questionNumber = (v2State.currentQuestionIndex ?? 0) + 1
      totalQuestions = v2State.questions?.length || 0
    } else {
      questionNumber =
        v1State.currentQuestionIndex !== null && v1State.currentQuestionIndex !== undefined
          ? v1State.currentQuestionIndex + 1
          : 1
      totalQuestions = v1State.questions?.length || 0
    }

    parts.push(`Question ${questionNumber}/${totalQuestions}`)
  }

  if (parts.length === 0) {
    return 'General feedback (no active challenge)'
  }

  return parts.join(' • ')
}
